import { createRef, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@apollo/client';
import { Loader2Icon, UploadCloudIcon } from 'lucide-react';
import { useDropzone } from 'react-dropzone';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogFooter,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useErrorHandler,
  useUploadFile,
} from '@sandtech-hub-monorepo/ui-kit';
import {
  Gender,
  UpdateProfilePhotoDocument,
  UpdateUserDocument,
  UploadScope,
} from '@sandtech-hub-monorepo/ehub-client';

import { ImageCropper } from './ImageCropper';
import { nameField, optionalTitleLikeField } from '../../../utils/validation';

const genderLabels: Record<Gender, string> = {
  [Gender.Female]: 'Female',
  [Gender.Male]: 'Male',
  [Gender.NonBinary]: 'Non-binary',
  [Gender.Other]: 'Others',
  [Gender.PreferNotToSay]: 'Prefer not to say',
};

const formSchema = z.object({
  firstName: nameField('First name'),
  lastName: nameField('Last name'),
  title: optionalTitleLikeField('Title', 100),
  gender: z.nativeEnum(Gender).optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface Props {
  open: boolean;
  handleOpenChange: (open: boolean) => void;
  defaultValues: FormValues;
  profilePhoto?: string | null;
}

export const ProfileEditDialog = ({
  open,
  handleOpenChange,
  defaultValues,
  profilePhoto,
}: Props) => {
  const [selectedFile, setSelectedFile] = useState<File>();
  const previewUrl = useMemo(
    () => (selectedFile ? URL.createObjectURL(selectedFile) : ''),
    [selectedFile]
  );
  const [imageForCrop, setImageForCrop] = useState('');
  const profilePhotoUploadRef = createRef<HTMLInputElement>();

  const { handleGeneralAppError } = useErrorHandler();
  const { uploadFile, loading: uploadFileLoading } = useUploadFile();
  const [updateProfilePhoto] = useMutation(UpdateProfilePhotoDocument, {
    onError: (error) => handleGeneralAppError(error.message),
  });
  const [updateUser, { loading }] = useMutation(UpdateUserDocument, {
    onError: (error) => handleGeneralAppError(error.message),
    onCompleted: () => handleOpenChange(false),
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  /*
   * Keyed on `open`, not on `defaultValues`. Both call sites build that object
   * inline, so its identity changes on every render of the parent — keyed on it,
   * the reset ran on every parent render and wiped whatever the learner had
   * typed. Any Apollo write to the `User` entity while the dialog is open is
   * enough to cause one.
   *
   * Opening is the only moment the form should take the current values, which
   * is what this now does. `form` and `defaultValues` are read inside rather
   * than depended on, deliberately.
   */
  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    } else {
      setSelectedFile(undefined);
      setImageForCrop('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFileChange = useCallback(
    (files: File[]) => {
      const file = files[0];

      if (file && file.type.substring(0, 5) === 'image') {
        setImageForCrop(URL.createObjectURL(file));
      } else {
        handleGeneralAppError(
          'The file you selected is not valid! Please try again with a PNG, JPG or JPEG.'
        );
      }
    },
    [handleGeneralAppError]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFileChange,
    multiple: false,
    accept: {
      'image/jpeg': [],
      'image/jpg': [],
      'image/png': [],
    },
  });

  const handleSubmit = async (values: FormValues) => {
    if (selectedFile) {
      const fileUrl = await uploadFile(UploadScope.ProfilePhoto, selectedFile);

      if (fileUrl) {
        await updateProfilePhoto({ variables: { input: { url: fileUrl } } });
      }
    }

    updateUser({
      variables: {
        input: {
          firstName: values.firstName,
          lastName: values.lastName,
          title: values.title,
          gender: values.gender,
        },
      },
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      title={imageForCrop ? 'Crop your profile photo' : 'Edit profile'}
      description={imageForCrop ? '' : 'Update your basic information'}
      className="bg-card"
    >
      {imageForCrop ? (
        <ImageCropper
          imageUrl={imageForCrop}
          onCrop={(file) => {
            setSelectedFile(file);
            setImageForCrop('');
          }}
          onDismiss={() => setImageForCrop('')}
        />
      ) : (
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="flex gap-2 sm:gap-5">
              <div className="h-24 w-24 my-auto rounded-full overflow-hidden shadow-lg shrink-0">
                <Avatar className="h-full w-full">
                  {previewUrl || profilePhoto ? (
                    <AvatarImage
                      src={previewUrl || profilePhoto || ''}
                      className="object-cover"
                    />
                  ) : (
                    <AvatarFallback className="flex text-2xl bg-background">
                      {`${defaultValues.firstName?.charAt(0) || ''}${
                        defaultValues.lastName?.charAt(0) || ''
                      }`}
                    </AvatarFallback>
                  )}
                </Avatar>
              </div>

              <div
                className="h-full p-2 flex-1 flex flex-col items-center justify-around rounded-md border cursor-pointer"
                {...getRootProps()}
              >
                <Input
                  {...getInputProps()}
                  className="hidden"
                  ref={profilePhotoUploadRef}
                  type="file"
                />
                <div
                  className="h-full w-full flex flex-col items-center justify-center rounded-md"
                  onClick={() => profilePhotoUploadRef.current?.click()}
                >
                  {isDragActive ? (
                    <div className="text-sm text-center">
                      <span className="font-medium text-primary">
                        Drop your file here
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="h-10 p-2 aspect-square rounded-full">
                        <UploadCloudIcon className="stroke-muted-foreground" />
                      </div>
                      <div className="text-sm text-center">
                        <span className="font-medium text-primary">
                          Click to upload
                        </span>
                        <span className="ml-1">or drag and drop</span>
                      </div>
                      <span className="text-[10px] xs:text-xs text-center text-muted-foreground">
                        PNG, JPG or JPEG
                      </span>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">First Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="font-normal">Last Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">Title</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="What is your title?" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-normal">Gender</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your gender" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {Object.values(Gender).map((value) => (
                        <SelectItem key={value} value={value}>
                          {genderLabels[value]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="mt-4 flex flex-col sm:flex-row gap-1">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={loading || uploadFileLoading}>
                {loading || uploadFileLoading ? (
                  <Loader2Icon className="animate-spin" />
                ) : (
                  'Save'
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      )}
    </Dialog>
  );
};
